/**
 * Per-section disable flag computation for the Shopper Experience tab
 * (CampaignFeatures.vue) and the Checkout Info tab
 * (CampaignCheckoutInfo.vue).
 *
 * Background: these tabs originally computed a single `disableFields`
 * boolean from four conditions (complete, active, has_orders,
 * isNewOrderAdded) and applied it to every editable control. That
 * over-locked admin UI whenever a campaign had any orders or was
 * currently active.
 *
 * The Order Settings reader audit (planning/MVP Roadmap/Phase 2 -
 * Core Integrations/Order Settings Snapshot Plan.md) and the Order
 * Record Durability follow-up (planning/MVP Roadmap/Phase 2 - Core
 * Integrations/Order Record Durability Plan.md) showed the platform
 * already applies current rules to frozen historical amounts
 * (CURRENT-RULE pattern). Once the remaining READS_LIVE bugs are
 * fixed on the backend, neither `has_orders` nor `active` is a
 * legitimate reason to lock most controls — only `complete` (campaign
 * is finalized) and `newOrder` (a fresh order was just placed in this
 * session) are.
 *
 * This helper exposes three flags so different surfaces can pick the
 * appropriate gate:
 *
 *   - disableForActiveOrComplete — locks on complete OR active OR
 *     newOrder. The historical default for Shopper Experience
 *     sections; retained for callers that still need it.
 *
 *   - disableForOrders — adds `has_orders` on top of
 *     disableForActiveOrComplete. Originally used by the
 *     payment-method radio and is_prevent_order_duplication holdouts.
 *
 *   - disableForCampaignLifecycle — locks ONLY on complete OR
 *     newOrder. The new minimum gate for the Checkout Info tab and
 *     for the Shopper Experience sections after the Order Record
 *     Durability relaxation. Drops both `has_orders` and `active`.
 *
 * @param {Object} options
 * @param {Object|null} options.campaign  Campaign object (may be null
 *                                        during initial render).
 * @param {boolean} options.isNewOrderAdded  Vuex getter
 *                                           `getIsNewOrderAdded`.
 * @returns {{
 *   disableForActiveOrComplete: boolean,
 *   disableForOrders: boolean,
 *   disableForCampaignLifecycle: boolean
 * }}
 */
export function computeDisableFlags({ campaign, isNewOrderAdded }) {
  const complete = !!(campaign && campaign.complete);
  const active = !!(campaign && campaign.active);
  const hasOrders = !!(campaign && campaign.has_orders);
  const newOrder = !!isNewOrderAdded;

  const disableForCampaignLifecycle = complete || newOrder;
  const disableForActiveOrComplete = disableForCampaignLifecycle || active;
  const disableForOrders = disableForActiveOrComplete || hasOrders;

  return {
    disableForActiveOrComplete,
    disableForOrders,
    disableForCampaignLifecycle,
  };
}
